import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { INK, SERIF, BODY } from './DagbladFrontPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/dagblad/kid-week`;
const cfg = { withCredentials: true };
const DAYS = ['M', 'T', 'O', 'T', 'F', 'L', 'S'];
const COLORS = ['#FFE3EC', '#E3F0FF'];

function cheer(stars, name) {
  if (stars >= 5) return `Wow, ${name}! Du är veckans superstjärna 🌟`;
  if (stars >= 1) return `${stars} packade dagar, ${name}! Farmor är så stolt 💛`;
  return `Nästa vecka samlar vi stjärnor tillsammans, ${name} 🌱`;
}

function KidPicture({ kid, monday, ready, drawing }) {
  if (ready) return <img src={`${API}/${kid.id}/picture?m=${monday}`} alt={`${kid.name}s veckodjur`} className="w-full h-full object-cover" data-testid={`kid-week-img-${kid.id}`} />;
  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-center px-2" data-testid={`kid-week-placeholder-${kid.id}`}>
      {drawing ? <Loader2 className="w-6 h-6 animate-spin" /> : <span className="text-4xl">🎨</span>}
      <span className="text-xs italic" style={{ fontFamily: BODY }}>{drawing ? `Farmor ritar ${kid.animal}…` : 'Bilden kommer snart'}</span>
    </div>
  );
}

function KidCard({ kid, i, monday, ready, drawing }) {
  return (
    <div className="rounded-3xl border-[3px] p-3 flex flex-col sm:flex-row gap-3 items-center" style={{ borderColor: INK, background: COLORS[i % 2] }} data-testid={`kid-week-${kid.id}`}>
      <div className="w-36 h-36 md:w-28 md:h-28 rounded-2xl overflow-hidden border-[3px] bg-white flex-shrink-0 rotate-1" style={{ borderColor: INK }}>
        <KidPicture kid={kid} monday={monday} ready={ready} drawing={drawing} />
      </div>
      <div className="flex-1 min-w-0 text-center sm:text-left">
        <h4 className="text-2xl font-black" style={{ fontFamily: SERIF }}>{kid.name}</h4>
        <div className="flex justify-center sm:justify-start gap-0.5 my-2" data-testid={`kid-week-stars-${kid.id}`}>
          {kid.days.map((d, j) => (
            <div key={d.date} className="flex flex-col items-center" title={d.packed ? 'Packad väska!' : ''}>
              <span className={`text-2xl md:text-lg transition-transform ${d.packed ? 'scale-110' : 'opacity-25 grayscale'}`} data-testid={`kid-week-star-${kid.id}-${j}`}>⭐</span>
              <span className="text-[10px] font-black opacity-60">{DAYS[j]}</span>
            </div>
          ))}
        </div>
        <p className="text-sm font-bold" style={{ fontFamily: BODY }} data-testid={`kid-week-cheer-${kid.id}`}>{cheer(kid.stars, kid.name)}</p>
      </div>
    </div>
  );
}

export default function DagbladKidWeek() {
  const [d, setD] = useState(null);
  const [ready, setReady] = useState({});
  const [drawing, setDrawing] = useState('');
  useEffect(() => {
    let alive = true;
    axios.get(API, cfg).then(async (r) => {
      if (!alive) return;
      setD(r.data);
      setReady(Object.fromEntries(r.data.kids.map((k) => [k.id, k.has_picture])));
      for (const k of r.data.kids.filter((x) => !x.has_picture)) {
        if (!alive) return;
        setDrawing(k.id);
        try { await axios.post(`${API}/${k.id}/picture`, {}, { ...cfg, timeout: 120000 }); if (alive) setReady((c) => ({ ...c, [k.id]: true })); } catch { /* placeholder stays */ }
      }
      if (alive) setDrawing('');
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!d?.kids.length) return null;
  return (
    <section className="rounded-3xl border-[3px] p-4 sm:p-5 shadow-[4px_4px_0_#2B2620] bg-[#FFFBF2] space-y-3" style={{ borderColor: INK }} data-testid="kid-week">
      <h3 className="text-2xl font-black" style={{ fontFamily: SERIF }}>Barnens söndagsbild 🎒</h3>
      <p className="text-sm" style={{ fontFamily: BODY }}>En stjärna för varje dag väskan blev packad.</p>
      <div className="grid md:grid-cols-2 gap-3">
        {d.kids.map((k, i) => <KidCard key={k.id} kid={k} i={i} monday={d.monday} ready={ready[k.id]} drawing={drawing === k.id} />)}
      </div>
    </section>
  );
}
